import React, { type JSX } from 'react'
import { Box, Paragraph } from '@alto-avios/alto-ui'
import { useTranslations } from 'next-intl'
import { MinMaxQuantity, TravelerType } from './types'

interface LabelProps {
  type: TravelerType
  minMaxQuantity?: MinMaxQuantity
  ageRange: [number, number]
}

const TravelerTypeLabel = ({
  type,
  minMaxQuantity: { minCount = 0, maxCount = 0 } = {},
  ageRange,
}: LabelProps): JSX.Element => {
  const t = useTranslations('common.labels')

  if (minCount < 0 || maxCount < 0) {
    throw new Error('Minimum and maximum quantities cannot be less than zero')
  }

  if (minCount > maxCount) {
    throw new Error('Minimum quantity cannot be greater than maximum quantity')
  }

  const mapper = {
    [TravelerType.senior]: t('senior'),
    [TravelerType.adult]: t('adult'),
    [TravelerType.youth]: t('youth'),
    [TravelerType.child]: t('child'),
    [TravelerType.infant]: t('infant'),
    [TravelerType.traveler]: t('traveller'),
  }

  const aged = {
    [TravelerType.traveler]: t('aged_range', { range: 'No age restrictions' }),
    [type]: ageRange && t('aged_range', { range: ageRange.join('-') }),
  }

  return (
    <Box display="flex" flexDirection="column">
      <Paragraph size="md" foregroundColor="primary">
        {mapper[type]} ({aged[type]})
      </Paragraph>
      {(minCount > 0 || maxCount > 0) && (
        <Paragraph size="sm">
          {minCount > 0 && t('minimum_quantity', { quantity: minCount })}{' '}
          {maxCount > 0 && t('maximum_quantity', { quantity: maxCount })}
        </Paragraph>
      )}
    </Box>
  )
}

export default TravelerTypeLabel
